import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/lib/auth';
import { ymGoal, ymGoalThen } from '@/lib/analytics';
import { isProOrLifetime } from '@/constants/tiers';
import { giftDaysLeft } from '@/lib/proDays';
import { trackHeaderBar } from '@/lib/headerBars';

/*
 * Сквозная плашка над верхним меню.
 *
 * Живёт ВНУТРИ MainNavbar, поэтому появляется везде, где есть меню (главная,
 * хорар, экраны ответов, страницы программ, статьи), и нигде не дублируется.
 *
 * Три состояния:
 *   1) гость                          -> зовём регистрироваться
 *   2) зарегистрирован, даты рождения нет -> зовём заполнить данные
 *   3) дата есть, рассылка выключена   -> зовём включить прогнозы
 * Всё заполнено и рассылка включена — плашки нет.
 *
 * N считается от ДАТЫ РЕГИСТРАЦИИ — общим правилом сервера (backend/gift_days.py,
 * поле gift_days_left в /auth/me), тем же, что у счётчика под меню, кабинета
 * и дневного письма. Протянул пять дней — видишь меньшее число.
 *
 * Счётчик подарочных дней живёт не здесь, а ПОД меню (ProDaysStrip в
 * MainNavbar): эта плашка — только над меню.
 *
 * Крестик закрывает плашку на сессию вкладки, а не навсегда: следующий визит —
 * плашка снова на месте.
 */

const DISMISS_KEY = 'promo_bar_dismissed';
const STATUS_KEY = 'promo_bar_status';

// Плашка монтируется заново на каждой странице (меню рендерит каждая страница
// сама), поэтому статус тянем один раз на сессию и держим в памяти.
let statusMemo = null;

// Кто сейчас показан на экране и должен узнать о сбросе кэша. Сброс приходит
// из чужого кода (засев профиля из гостевой карты, сохранение даты рождения),
// а плашка к тому моменту уже смонтирована и держит своё состояние: одного
// удаления кэша ей мало — без оповещения она так и висит с «Заполни дату
// рождения» у человека, у которого дата уже есть.
const statusListeners = new Set();

function readCachedStatus() {
  if (statusMemo) return statusMemo;
  try {
    const raw = sessionStorage.getItem(STATUS_KEY);
    if (raw) {
      statusMemo = JSON.parse(raw);
      return statusMemo;
    }
  } catch (_e) {}
  return null;
}

function writeCachedStatus(next) {
  statusMemo = next;
  try {
    sessionStorage.setItem(STATUS_KEY, JSON.stringify(next));
  } catch (_e) {}
}

// Дёргать после сохранения даты рождения или переключения рассылки,
// чтобы плашка пересчиталась и не висела с устаревшим состоянием.
export function resetPromoBarStatus() {
  statusMemo = null;
  try {
    sessionStorage.removeItem(STATUS_KEY);
  } catch (_e) {}
  statusListeners.forEach((fn) => {
    try {
      fn();
    } catch (_e) {}
  });
}

function plural(n, one, few, many) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

export default function PromoStickyBar() {
  // loading — авторизация ещё проверяется. До ответа /auth/me вошедшему
  // человеку показывалась гостевая плашка «Начать бесплатно»; вместо угадывания
  // не показываем плашку вовсе, пока состояние не известно.
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const barRef = useRef(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (_e) {
      return false;
    }
  });
  const [status, setStatus] = useState(() => readCachedStatus());
  // Счётчик пересчётов: растёт на каждый resetPromoBarStatus снаружи и
  // перезапускает эффект ниже. Без него порядок событий при регистрации из
  // окна карты давал ложную плашку: вход уже состоялся, эффект сходил за
  // профилем РАНЬШЕ, чем засеялась дата из гостевой формы, и запомнил
  // «даты нет» на всю сессию вкладки.
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    const onReset = () => {
      setStatus(null);
      setReloadTick((n) => n + 1);
    };
    statusListeners.add(onReset);
    return () => {
      statusListeners.delete(onReset);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const cached = readCachedStatus();
    if (cached) {
      setStatus(cached);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem('auth_token');
      // Та же лёгкая обёртка, что и в lib/auth.js. Форму ответа оставляем
      // axios-совместимой ({ data }), чтобы разбор ниже не менялся: любая
      // ошибка и любой не-2xx по-прежнему дают null и плашку не показывают.
      const get = (path) =>
        api('GET', path, null, token)
          .then((data) => ({ data }))
          .catch(() => null);
      const [profileRes, prefsRes] = await Promise.all([
        get('/natal/profile'),
        get('/users/preferences'),
      ]);
      if (cancelled) return;
      // Если хоть один запрос не ответил — молчим. Лучше не показать плашку,
      // чем показать неверную.
      if (!profileRes || !prefsRes) return;
      const next = {
        hasBirthDate: !!(profileRes.data && profileRes.data.has_profile && profileRes.data.profile && profileRes.data.profile.birth_date),
        mailingOn: prefsRes.data ? prefsRes.data.email_predictions_enabled !== false : true,
      };
      writeCachedStatus(next);
      setStatus(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, reloadTick]);

  let variant = null;
  if (!isAuthenticated) variant = 'guest';
  // Профиль рождения уже есть — в том числе засеянный из гостевой карты при
  // регистрации: звать заполнять то, что заполнено, нельзя.
  else if (status && status.hasBirthDate) variant = status.mailingOn ? null : 'no_mailing';
  else if (status && !status.hasBirthDate) variant = 'no_birth';
  if (dismissed) variant = null;
  if (authLoading) variant = null;

  // Плашка стоит самой верхней полосой, меню и весь документ съезжают вниз ровно
  // на её измеренную высоту (lib/headerBars.js). Поэтому отступы страниц под
  // меню (pt-16) продолжают работать как были, и плашка ничего не перекрывает —
  // в том числе ответ хорара.
  useEffect(() => trackHeaderBar('top', barRef.current), [variant]);

  if (!variant) return null;

  const n = giftDaysLeft(user);
  // У ПРО и бессрочных никакого «осталось N» не существует: срока у них нет,
  // а days_remaining приходит фиктивным 99999 — счёт и выдавал «осталось
  // 99999 бесплатных прогнозов из 14». Таким показываем ту же плашку без
  // счётчика. Триал считает как считал.
  // Числа нет и у тех, у кого подарок кончился или тариф оплачен (ЛАЙТ):
  // им — тот же текст без счёта, а не «осталось 0».
  const unlimited = isProOrLifetime(user) || !n;
  let text = '';
  let cta = '';
  let onCta = () => {};

  if (variant === 'guest') {
    // В полосе под меню помещается ровно одно обещание: длинный текст
    // обрезался многоточием как раз на выгоде. Подробности про подстановку
    // переехали в модалку лимита и на второй экран регистрации.
    text = 'Твой прогноз на каждый день — 14 дней бесплатно';
    cta = 'Начать бесплатно';
    onCta = () => ymGoalThen('signup_click', { place: 'promo_bar' }, () => navigate('/auth/register'));
  } else if (variant === 'no_birth') {
    text = unlimited
      ? 'Заполни дату рождения, чтобы получать прогнозы'
      : `Заполни дату рождения — и завтра утром придёт первый прогноз. У тебя ${plural(n, 'остался', 'осталось', 'осталось')} ${n} ${plural(n, 'бесплатный прогноз', 'бесплатных прогноза', 'бесплатных прогнозов')} из 14, каждый день их становится меньше.`;
    cta = 'Заполнить данные';
    onCta = () => {
      ymGoal('promo_bar_click', { place: 'no_birth' });
      navigate('/lk/profile');
    };
  } else {
    // Тот же фиктивный счёт стоял и здесь: «осталось 99999 дней бесплатного
    // доступа» у человека с оплаченным ПРО.
    text = unlimited
      ? (isProOrLifetime(user) || (user && user.effective_tier >= 2)
        ? 'Включи ежедневные прогнозы — они входят в твой тариф.'
        : 'Включи ежедневные прогнозы — каждое утро на почту.')
      : `Включи ежедневные прогнозы — ${plural(n, 'остался', 'осталось', 'осталось')} ${n} ${plural(n, 'день', 'дня', 'дней')} бесплатного доступа.`;
    cta = 'Включить';
    onCta = () => {
      ymGoal('promo_bar_click', { place: 'no_mailing' });
      navigate('/email-preferences');
    };
  }

  const close = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (_e) {}
  };

  return (
    <div
      ref={barRef}
      className="fixed left-0 right-0 z-50 bg-accent-cta-500 text-white"
      style={{ top: 'var(--imp-h, 0px)' }}
      data-testid="promo-sticky-bar"
      data-variant={variant}
    >
      {/* Вся полоса — одна зона нажатия: в кнопку шириной 148 px на телефоне
          палец попадает не всегда. Кнопку оставляем видимой, чтобы человек
          понимал, что здесь нажимают. Кнопка и крестик гасят всплытие. */}
      <div
        className="max-w-6xl mx-auto px-3 py-1.5 flex items-center justify-between gap-3 cursor-pointer"
        onClick={onCta}
        data-testid="promo-sticky-bar-hit"
      >
        <span className="text-xs md:text-sm font-medium leading-snug text-left" data-testid="promo-sticky-bar-text">
          {text}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onCta(); }}
            className="text-xs md:text-sm font-bold bg-white text-accent-cta-600 px-3 py-1 rounded-lg hover:bg-sky-50 transition-colors whitespace-nowrap"
            data-testid="promo-sticky-bar-cta"
          >
            {cta} →
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Скрыть"
            className="text-white/70 hover:text-white px-1.5 py-1 text-lg leading-none"
            data-testid="promo-sticky-bar-dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
